<template>
    <TablePage
        class="px-0 2xl:px-10 pb-2"
        :module-name="moduleName"
        :response="response"
        :isLoading="isLoading"
        :tableHeaders="tableHeaders"
        :filters="userFilter"
        :bulkOperations="userBulkOperations"
        :menuOptions="menuOptions"
        @refetch-results="loadUsers"
        @clear-filter="clearFilter"
        @change-filters="setFilters"
        @set-filter-options="setFilterOptions"
        @open-add-modal="openAddPersonForm"
    >
        <!-- <template #bulk-operations="{ events: {}}"> -->
            <!-- <CompanyCampaignChangeBulkOperations
                :bulkAction="bulkAction"
                :selectedResults="selectedResults"
                :campaigns="campaigns"
                @cancel-bulk-operation="hideBulkActionMenu"
                @close-bulk-operation="hideBulkOperations"
                @clear-selected-results="clearSelectAllClearSelectedResults"
                @refetch-results="refetchResults(fetchFilter)"
            /> -->
        <!-- </template> -->
         <template #create-form>
             <base-modal :is-form-visible="addPersonFormVisible" @close="closeAddUserForm">
             <template v-slot:header> Create Person </template>
             <template v-slot:body>
                 <PersonForm
                    class="mt-2"
                     @refetch-people="loadUsers(currentFetchFilter)"
                     @close="closeAddUserForm"
                 />
             </template>
             </base-modal>
         </template>
        <template #table-row="{ events: { refetchResults }, selectAll, fetchFilter, userPermissions }">
            <PersonRow
                v-for="person in response.data.results"
                :key="person.id"
                :person="person"
                :selectAll="selectAll"
                :groups="groups"
                :userPermissions="userPermissions"
                :workflow-steps="workflowSteps"
                :is-loading="isLoading"
                @refetch-results="refetchResults(fetchFilter)"
                @loading="setLoading"
            />
        </template>
    </TablePage>
</template>

<script setup>
import { ref, reactive, computed} from 'vue';
import { useStore } from 'vuex';
import TablePage from "@/components/base-components/TablePage";
import PersonRow from './PersonRow.vue';
import PersonRowForm from './PersonRowForm.vue';
import { useToast } from 'vue-toast-notification';
import { formatDate } from '@/composables/formatDate';
import { getFullUsers } from '@/services/User'
import { getPeople } from '@/services/People';
import { getGroups } from '@/services/Group';
import { getWorkflowSteps } from "@/services/Workflow.js";
import PersonForm from './PersonForm.vue';

const store = useStore()
const $toast = useToast()
const isLoading = ref(false)
const moduleName = ref('Users')
const response = ref(null)
const workflowSteps = ref([])
const addPersonFormVisible = ref(false)
const groups = ref([])
async function loadWorkflowSteps(){
    const token = store.state.auth.token
    const result = await getWorkflowSteps(token, '?workflow_type=employee_onboarding&status=active')
    if(result.status === 200){
        workflowSteps.value = result.data
        // console.log('workflowSteps', workflowSteps.value)
    }
}

function openAddPersonForm(){
    console.log('open add person form')
    addPersonFormVisible.value = true
}

function closeAddUserForm(){
    addPersonFormVisible.value = false
}

const hasFullPeoplePermissions = computed(() => {
    return store.state.auth.userPermissions.isDev || store.state.auth.userPermissions.isManagement || store.state.auth.userPermissions.isSuperuser
})

loadWorkflowSteps()
const tableHeaders = ref([
    {
        value: 'first_name',
        label: 'First Name',
        class: 'p-0 w-24  whitespace-nowrap'
    },
    {
        value: 'last_name',
        label: 'Last Name',
        class: 'p-0 w-24  whitespace-nowrap'
    },
    {
        value: 'username',
        label: 'Username',
        class: 'p-0 w-36  whitespace-nowrap'
    },
    {
        value: 'email',
        label: 'Email',
        class: 'p-0 w-20 lg:w-40  whitespace-nowrap '
    },
    {
        value: 'is_active',
        label: 'Is Active',
        class: 'p-0 w-16 lg:w-16  whitespace-nowrap '
    },
    {
        value: 'export_limits',
        label: 'Export Limits',
        class: 'p-0 w-24 lg:w-24  whitespace-nowrap ',
        devOrMgmt: true
    },
    {
        value: 'workflow_steps',
        label: 'Missing Workflow Steps',
        class: 'p-0 w-44 lg:w-44  whitespace-nowrap '
    },
    {
        value: 'groups',
        label: 'Groups',
        class: 'p-0 w-36  whitespace-nowrap',
        devOrMgmt: true
    }
])
// const defaultOrdering = ref(['-date'])

function constructUserFilter(){
    if(hasFullPeoplePermissions.value){
        return {
        firstName: {
        name: "first_name",
        label: "First Name",
        value: null,
        defaultValue: null,
        type: 'text',
        widget: 'input',
        multiSelect: false,
        visible: true,
        },
        lastName: {
            name: "last_name",
            label: "Last Name",
            value: null,
            defaultValue: null,
            type: 'text',
            widget: 'input',
            multiSelect: false,
            visible: true,
        },
        isActive: {
            name: "user_is_active",
            label: "Is Active",
            value: null,
            defaultValue: "True",
            options: [
                {value: null, label: "------"},
                {value: "True", label: "True"},
                {value: "False", label: "False"},
            ],
            widget: 'list-box',
            multiSelect: false,
            awaitingOptions: false,
            visible: true
        },
        hasUploadedPhotos: {
            name: "has_uploaded_photos",
            label: "Has Uploaded Photos",
            value: null,
            defaultValue: null,
            options: [
                {value: null, label: "------"},
                {value: "True", label: "True"},
                {value: "False", label: "False"},
            ],
            widget: 'list-box',
            multiSelect: false,
            awaitingOptions: false,
            visible: true
        },
        photoConsentStatus: {
            name: "photo_consent_status",
            label: "Photo Consent Status",
            value: null,
            defaultValue: null,
            options: [
                {value: null, label: "------"},
                {value: "none", label: "None"},
                {value: "consent", label: "Consent"},
                {value: "non-consent", label: "Non-Consent"},
            ],
            widget: 'list-box',
            multiSelect: false,
            awaitingOptions: false,
            visible: true
        },
        groups: {
            name: "user_groups",
            label: "Groups",
            value: [],
            defaultValue: [],
            options: [
                {value: "clear", label: "------------"}
            ],
            widget: 'list-box',
            multiSelect: true,
            setOptionsMethod: setGroupOptions.bind(null),
            awaitingOptions: false,
            visible: true
        }
    }
    } else {
        return {
        firstName: {
        name: "first_name",
        label: "First Name",
        value: null,
        defaultValue: null,
        type: 'text',
        widget: 'input',
        multiSelect: false,
        visible: true,
        },
        lastName: {
            name: "last_name",
            label: "Last Name",
            value: null,
            defaultValue: null,
            type: 'text',
            widget: 'input',
            multiSelect: false,
            visible: true,
        },
        isActive: {
            name: "user_is_active",
            label: "Is Active",
            value: null,
            defaultValue: "True",
            options: [
                {value: null, label: "------"},
                {value: "True", label: "True"},
                {value: "False", label: "False"},
            ],
            widget: 'list-box',
            multiSelect: false,
            awaitingOptions: false,
            visible: true
        },
        hasUploadedPhotos: {
            name: "has_uploaded_photos",
            label: "Has Uploaded Photos",
            value: null,
            defaultValue: null,
            options: [
                {value: null, label: "------"},
                {value: "True", label: "True"},
                {value: "False", label: "False"},
            ],
            widget: 'list-box',
            multiSelect: false,
            awaitingOptions: false,
            visible: true
        },
        photoConsentStatus: {
            name: "photo_consent_status",
            label: "Photo Consent Status",
            value: null,
            defaultValue: null,
            options: [
                {value: null, label: "------"},
                {value: "none", label: "None"},
                {value: "consent", label: "Consent"},
                {value: "non-consent", label: "Non-Consent"},
            ],
            widget: 'list-box',
            multiSelect: false,
            awaitingOptions: false,
            visible: true
        }
    }
    }
}


const userFilter = reactive(constructUserFilter())


function setLoading(){
    isLoading.value = true
}

async function setGroupOptions() {
    if(hasFullPeoplePermissions.value){
        const token = store.state.auth.token
        const result = await getGroups(token)
        groups.value = result.data
        userFilter.groups.options = [{value: "clear", label: "------------"}, ...result.data.map((group) => {
                return {value: group.id, label: group.name}
                }
            )]
    }
}

const userBulkOperations = reactive({})

const menuOptions = reactive({
    selectAll: true,
    paginationArrows: true,
    totalResults: false,
    pageSize: true,
    refreshButton: true,
    filters: true,
    bulkOperations: false,
    addModal: hasFullPeoplePermissions.value,
    addFormRows: false
})

function setFilters(updatedFilters) {
    userFilter.firstName = updatedFilters.firstName;
    userFilter.lastName = updatedFilters.lastName;
    if(hasFullPeoplePermissions.value){
        userFilter.groups = updatedFilters.groups;
    }
}

const currentFetchFilter = ref('')

async function loadUsers(fetchFilter='') {
    isLoading.value = true;
    currentFetchFilter.value = fetchFilter
    const token = store.state.auth.token
    response.value = await getPeople(token, fetchFilter);
    isLoading.value = false;
}

async function setFilterOptions(filterName, queryParams='') {
    console.log('setFilterOptions', filterName)
    if(!userFilter[filterName].awaitingOptions){
        if(userFilter[filterName].setOptionsMethod){
            userFilter[filterName].awaitingOptions = true
            userFilter[filterName].setOptionsMethod(queryParams)
            setTimeout(() => {
                userFilter[filterName].awaitingOptions = false;
            }, 30000)
        }
    }
}


function clearFilter(filterName) {
    console.log('filter name', filterName)
    if(Array.isArray(userFilter[filterName].value)){
        userFilter[filterName].value = []
    } else {
        userFilter[filterName].value = null
    }
}

setGroupOptions()

// loadUsers()

</script>